import { useAuth } from '@/context/AuthContext';
import { useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingCart, Package, Warehouse, GitBranch,
  Settings, FileText, BarChart3, Plug, Blocks, CreditCard,
  LogOut, ChevronLeft, ChevronRight, Tag, Contact, UserCircle, Shield, TicketCheck, Megaphone, FolderTree
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const navGroups = [
  {
    label: 'misc_nav.main',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'misc_nav.home', feature: 'dashboard' },
      { to: '/pos', icon: ShoppingCart, label: 'misc_nav.pos', feature: 'pos' },
      { to: '/sales', icon: CreditCard, label: 'misc_nav.sales', feature: 'sales' },
    ],
  },
  {
    label: 'misc_nav.catalog',
    items: [
      { to: '/products', icon: Package, label: 'misc_nav.products', feature: 'products' },
      { to: '/categories', icon: FolderTree, label: 'misc_nav.categories', feature: 'products' },
      { to: '/pricing', icon: Tag, label: 'misc_nav.prices', feature: 'products' },
      { to: '/inventory', icon: Warehouse, label: 'misc_nav.inventory', feature: 'inventory' },
    ],
  },
  {
    label: 'misc_nav.management',
    items: [
      { to: '/customers', icon: Contact, label: 'misc_nav.customers', feature: 'customers' },
      { to: '/murabaha', icon: FileText, label: 'misc_nav.contracts', feature: 'murabaha' },
      { to: '/marketing', icon: Megaphone, label: 'misc_nav.marketing', feature: 'reports' },
      { to: '/tickets', icon: TicketCheck, label: 'misc_nav.tickets', feature: 'reports' },
      { to: '/reports', icon: BarChart3, label: 'misc_nav.reports', feature: 'reports' },
    ],
  },
  {
    label: 'misc_nav.integration',
    items: [
      
      { to: '/connectors', icon: Plug, label: 'misc_nav.connectors', feature: 'connectors' },
      { to: '/modules', icon: Blocks, label: 'misc_nav.integrations', feature: 'modules' },
    ],
  },
];

const bottomNavItems = [
  { to: '/audit', icon: Shield, label: 'misc_nav.audit', feature: 'settings' },
  { to: '/profile', icon: UserCircle, label: 'misc_nav.profile', feature: 'settings' },
];

export default function BackofficeLayout({ children }: { children: React.ReactNode }) {
  const { user, logout, canAccess } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const filteredBottomNav = bottomNavItems.filter(item => canAccess(item.feature) || user?.role === 'owner');

  const filteredGroups = navGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => canAccess(item.feature) || user?.role === 'owner'),
    }))
    .filter(group => group.items.length > 0);

  return (
    <div className="flex h-screen bg-background">
      <aside className={cn(
        "flex flex-col border-r border-border bg-card transition-all duration-200",
        collapsed ? "w-16" : "w-60"
      )}>
        <div className="flex items-center justify-between h-14 px-4 border-b border-border">
          {!collapsed && <span className="font-semibold text-foreground text-sm tracking-tight">Hisob POS</span>}
          <button onClick={() => setCollapsed(!collapsed)} className="p-1 rounded-md hover:bg-accent text-muted-foreground">
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-2 px-2">
          {filteredGroups.map(group => (
            <div key={group.label} className="mb-3">
              {!collapsed && (
                <span className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {t(group.label)}
                </span>
              )}
              <div className="mt-1 space-y-0.5">
                {group.items.map(item => {
                  const active = location.pathname.startsWith(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                        active
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                      )}
                    >
                      <item.icon size={18} />
                      {!collapsed && <span>{t(item.label)}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-border px-2 py-2 space-y-0.5">
          {filteredBottomNav.map(item => {
            const active = location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )}
              >
                <item.icon size={18} />
                {!collapsed && <span>{t(item.label)}</span>}
              </Link>
            );
          })}

          {!collapsed && user && (
            <div className="mt-2 px-3 py-2">
              <p className="text-xs font-medium text-foreground truncate">{user.full_name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
            </div>
          )}
          {!collapsed && (
            <div className="px-3 py-1">
              <LanguageSwitcher />
            </div>
          )}
          <button onClick={logout} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors">
            <LogOut size={16} />
            {!collapsed && <span>{t('common.logout')}</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
}
